import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import { StaticQuery, graphql } from "gatsby";
import { imageUrlFor } from "../lib/image-url";
import { buildImageObj } from "../lib/helpers";
import favicon96 from "../images/favicon/favicon-96x96.png";
import favicon32 from "../images/favicon/favicon-32x32.png";
import favicon16 from "../images/favicon/favicon-16x16.png";
import logoImage from "../images/capacity-logo.svg";

// Not stored in Sanity — hardcoded here (and again in footer.js's social
// icons) since there's nowhere else in the CMS these live yet. If the
// social roster ever changes, update both places.
const SITE_URL = "https://capacity.gg";
const SOCIAL_PROFILES = [
  "https://www.instagram.com/teamcapacity/",
  "https://vimeo.com/capacity",
  "https://www.youtube.com/channel/UCNGvjLPU9PzpcK-1F3hydyw/",
];

// Added 2026-09-25 — free analytics, no paid add-ons. GA4 (traffic/audience)
// and Microsoft Clarity (heatmaps/session recordings) are complementary, not
// redundant — see the privacy policy update made the same day covering both
// (Tracking Technologies section now discloses cookies; derivative-data
// categories cover log/usage, device, and location data). Neither tool has
// Google Signals/ad personalization or Clarity's advertising features
// enabled, so no consent-banner is needed for this setup.
const GA_MEASUREMENT_ID = "G-LESTGQS3PX";
const CLARITY_PROJECT_ID = "ynxx1p2665";

// Added 2026-09-23 alongside the FAQ page's FAQPage schema (see faq.js) —
// this Organization block renders on every page via SEO, giving AI
// crawlers/Overviews a consistent, structured "who is this site" anchor
// instead of having to infer it from prose. `schema` lets any page pass in
// additional JSON-LD (e.g. FAQPage) to render alongside it, without every
// call site needing to know about Organization itself.
function SEO({ description, lang, meta, keywords, title, image, schema }) {
  return (
    <StaticQuery
      query={detailsQuery}
      render={(data) => {
        const metaDescription = description || (data.site && data.site.description) || "";
        const siteTitle = (data.site && data.site.title) || "";
        const siteAuthor = (data.site && data.site.author && data.site.author.name) || "";
        const metaImage =
          image && image.asset ? imageUrlFor(buildImageObj(image)).width(1200).url() : "";

        const organizationSchema = {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: siteTitle,
          url: SITE_URL,
          logo: `${SITE_URL}${logoImage}`,
          sameAs: SOCIAL_PROFILES,
        };
        if (metaDescription) organizationSchema.description = metaDescription;

        const schemaBlocks = [organizationSchema, ...(schema || [])];

        return (
          <Helmet
            htmlAttributes={{ lang }}
            title={title}
            titleTemplate={title === siteTitle ? "%s" : `%s | ${siteTitle}`}
            meta={[
              {
                name: "description",
                content: metaDescription,
              },
              {
                property: "og:title",
                content: title,
              },
              {
                property: "og:description",
                content: metaDescription,
              },
              {
                property: "og:type",
                content: "website",
              },
              {
                property: "og:image",
                content: metaImage,
              },
              {
                name: "twitter:card",
                content: "summary",
              },
              {
                name: "twitter:creator",
                content: siteAuthor,
              },
              {
                name: "twitter:title",
                content: title,
              },
              {
                name: "twitter:description",
                content: metaDescription,
              },
            ]
              .concat(
                keywords && keywords.length > 0
                  ? {
                      name: "keywords",
                      content: keywords.join(", "),
                    }
                  : []
              )
              .concat(meta)}
          >
            <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
            <link rel="icon" type="image/png" sizes="96x96" href={favicon96} />
            <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_MEASUREMENT_ID}');
              `}
            </script>
            <script>
              {`
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;
                    t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
              `}
            </script>
            {schemaBlocks.map((block, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(block)}
              </script>
            ))}
          </Helmet>
        );
      }}
    />
  );
}

SEO.defaultProps = {
  lang: "en",
  meta: [],
  keywords: [],
  schema: [],
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  // Additional JSON-LD blocks (e.g. FAQPage) rendered alongside the
  // Organization schema every page already gets — see the comment above.
  schema: PropTypes.arrayOf(PropTypes.object),
};

export default SEO;

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site: sanitySiteSettings(_id: { eq: "siteSettings" }) {
      title
      description
      keywords
    }
  }
`;
